import React, { useMemo, useId, useRef, useEffect, useState } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis, XAxis } from 'recharts';

const HistoryChartTooltip = ({ active, payload }) => {
  if (active && payload?.[0]) {
    const data = payload[0].payload;
    return (
      <div className="chart-tooltip">
        <p className="font-semibold text-sm" style={{ color: data.color }}>
          {data.adjective}
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--apple-text-tertiary)' }}>
          {data.fullTime} · {Math.round(data.value)}%
        </p>
      </div>
    );
  }
  return null;
};

export const HistoryChart = ({ history, darkMode, currentMoodColor }) => {
  // Use unique IDs for gradients to ensure proper updates when data changes
  const uniqueId = useId();
  const areaGradientId = `historyAreaGradient-${uniqueId}`;
  const strokeGradientId = `historyStrokeGradient-${uniqueId}`;
  const containerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(120);
  
  // Keep previous data to avoid showing empty state during refresh
  const [displayData, setDisplayData] = useState([]);
  
  const chartData = useMemo(() => {
    if (!history?.length) return [];
    // History comes from backend already sorted oldest to newest
    // Take last 50 entries and keep in chronological order (oldest to newest, left to right)
    const dataSlice = history.slice(-50);
    return dataSlice.map((item, idx) => {
      const timestamp = new Date(item.timestamp);
      return {
        ...item,
        index: idx,
        // Scale intensity to show more variation (multiply by 100, but keep domain at 0-100)
        value: item.intensity * 100,
        time: timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        fullTime: timestamp.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
        dayLabel: timestamp.toLocaleDateString([], { weekday: 'short' })
      };
    });
  }, [history]);
  
  // Update display data only when we have new valid data (never show loading after first load)
  useEffect(() => {
    if (chartData.length > 0) {
      setDisplayData(chartData);
    }
  }, [chartData]);

  // Calculate min/max for better scaling
  const { minValue, maxValue } = useMemo(() => {
    if (!displayData.length) return { minValue: 0, maxValue: 100 };
    const values = displayData.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    // Add padding for visual effect
    return { 
      minValue: Math.max(0, min - 10), 
      maxValue: Math.min(100, max + 10) 
    };
  }, [displayData]);

  // Create a fingerprint of the data to use as a key for forcing re-renders
  const dataFingerprint = useMemo(() => {
    if (!displayData.length) return 'empty';
    // Use first, middle, and last entries for fingerprint (performance optimization)
    const first = displayData[0];
    const mid = displayData[Math.floor(displayData.length / 2)];
    const last = displayData[displayData.length - 1];
    return `${first?.adjective}-${mid?.adjective}-${last?.adjective}-${displayData.length}`;
  }, [displayData]);
  
  // Measure container height to fill available space
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const container = containerRef.current;
        const parent = container.parentElement;
        if (parent) {
          // Get parent's available height minus the gradient strip and any padding
          const parentRect = parent.getBoundingClientRect();
          const headerHeight = 40; // Approximate panel header height
          const gradientHeight = 12; // Gradient strip height
          const padding = 32; // Total vertical padding
          const availableHeight = parentRect.height - headerHeight - gradientHeight - padding;
          setChartHeight(Math.max(100, Math.min(200, availableHeight)));
        }
      }
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    // Also update after a short delay to account for layout settling
    const timer = setTimeout(updateHeight, 100);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      clearTimeout(timer);
    };
  }, []);

  // Show nothing only if we've never had data
  if (!displayData.length) {
    return <div className="empty-state">No history yet</div>;
  }

  // Calculate gradient strip - sample evenly if too many data points
  const gradientData = displayData.length > 30 
    ? displayData.filter((_, i) => i % Math.ceil(displayData.length / 30) === 0)
    : displayData;

  return (
    <div className="history-chart-container" data-testid="history-chart" ref={containerRef}>
      {/* Multi-color gradient bar at top - sampled for performance */}
      <div className="history-gradient-strip">
        {gradientData.map((d, idx) => (
          <div 
            key={`${d.adjective}-${d.time}-${idx}`} 
            className="history-gradient-segment"
            style={{ backgroundColor: d.color }}
            title={`${d.adjective} - ${d.fullTime}`}
          />
        ))}
      </div>
      
      {/* Week range label */}
      <div className="history-range-label" style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        fontSize: '10px', 
        color: 'var(--apple-text-tertiary)',
        marginTop: '4px',
        marginBottom: '2px'
      }}>
        <span>{displayData[0]?.dayLabel}</span>
        <span>{displayData.length} updates</span>
        <span>{displayData[displayData.length - 1]?.dayLabel}</span>
      </div>
      
      {/* Area chart with gradient fill - expands to fill available space */}
      <div className="history-area-chart" style={{ height: `${chartHeight}px`, width: '100%', flex: 1 }}>
        {chartHeight > 0 && (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <AreaChart data={displayData} margin={{ top: 5, right: 0, left: 0, bottom: 0 }} key={dataFingerprint}>
            <defs>
              <linearGradient id={areaGradientId} x1="0" y1="0" x2="1" y2="0">
                {gradientData.map((d, idx) => (
                  <stop 
                    key={idx}
                    offset={`${(idx / (gradientData.length - 1)) * 100}%`} 
                    stopColor={d.color} 
                    stopOpacity={0.6}
                  />
                ))}
              </linearGradient>
              <linearGradient id={strokeGradientId} x1="0" y1="0" x2="1" y2="0">
                {gradientData.map((d, idx) => (
                  <stop 
                    key={idx}
                    offset={`${(idx / (gradientData.length - 1)) * 100}%`} 
                    stopColor={d.color} 
                    stopOpacity={1}
                  />
                ))}
              </linearGradient>
            </defs>
            <YAxis domain={[minValue, maxValue]} hide />
            <Tooltip content={<HistoryChartTooltip />} />
            <Area
              type="monotone"
              dataKey="value"
              stroke={`url(#${strokeGradientId})`}
              strokeWidth={2}
              fill={`url(#${areaGradientId})`}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
